import { Component } from '@theme/component';

/** A native-details desktop accordion and an all-open mobile scroll-snap carousel. */
class SeriesAccordion extends Component {
  requiredRefs = ['track'];
  #abort;
  #panels = [];
  #dots = [];
  #index = 0;
  #revealTimer;
  #scrollFrame;
  #mobile = window.matchMedia('(max-width: 767px)');
  #finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  #reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    super.connectedCallback();
    this.#initialize();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#initialize();
  }

  disconnectedCallback() {
    this.#dispose();
    super.disconnectedCallback();
  }

  #dispose() {
    this.#abort?.abort();
    window.clearTimeout(this.#revealTimer);
    window.cancelAnimationFrame(this.#scrollFrame);
  }

  #initialize() {
    this.#dispose();
    this.#abort = new AbortController();
    this.#panels = [...this.querySelectorAll('[data-series-panel]')];
    this.#dots = [...this.querySelectorAll('[data-series-dot]')];
    if (!this.#panels.length) return;
    this.#index = Math.min(this.#index, this.#panels.length - 1);
    const { signal } = this.#abort;
    // Layout-generated boundary events must not select a different panel mid-transition.
    this.addEventListener('pointermove', this.#onPointer, { signal, passive: true });
    this.addEventListener('focusin', this.#onFocus, { signal });
    this.addEventListener('keydown', this.#onKey, { signal });
    this.refs.track.addEventListener('scroll', this.#onScroll, { signal, passive: true });
    this.#mobile.addEventListener('change', this.#syncMode, { signal });
    this.#reduceMotion.addEventListener('change', this.#syncMode, { signal });
    document.addEventListener('shopify:block:select', this.#onEditorSelect, { signal });
    this.setAttribute('data-enhanced', '');
    this.#syncMode();
  }

  #syncMode = () => {
    if (this.#mobile.matches) {
      for (const panel of this.#panels) panel.removeAttribute('name');
      for (const panel of this.#panels) {
        panel.open = true;
        panel.querySelector('summary').tabIndex = -1;
        panel.setAttribute('data-copy-visible', '');
      }
      if (this.refs.dots) this.refs.dots.hidden = this.#panels.length < 2;
      this.#markDot();
    } else {
      for (const panel of this.#panels) {
        panel.open = false;
        panel.setAttribute('name', `SeriesPanels-${this.id}`);
        panel.querySelector('summary').tabIndex = 0;
      }
      if (this.refs.dots) this.refs.dots.hidden = true;
      this.#activate(this.#index, true);
    }
  };

  #activate(index, immediate = false) {
    if (!Number.isInteger(index) || index < 0 || index >= this.#panels.length) return;
    if (this.#mobile.matches) {
      this.#index = index;
      this.#markDot();
      return;
    }
    const unchanged = this.#index === index && this.#panels[index].open;
    this.#index = index;
    if (unchanged && !immediate) return;
    window.clearTimeout(this.#revealTimer);
    for (const [i, panel] of this.#panels.entries()) {
      panel.open = i === index;
      panel.removeAttribute('data-copy-visible');
    }
    const selected = this.#panels[index];
    if (immediate || this.#reduceMotion.matches) selected.setAttribute('data-copy-visible', '');
    else this.#revealTimer = window.setTimeout(() => selected.setAttribute('data-copy-visible', ''), 450);
  }

  #panelFrom(event) {
    if (!(event.target instanceof Element)) return;
    const panel = event.target.closest('[data-series-panel]');
    return panel && this.contains(panel) ? panel : undefined;
  }

  #onPointer = (event) => {
    if (this.#mobile.matches || !this.#finePointer.matches || event.pointerType === 'touch') return;
    const panel = this.#panelFrom(event);
    if (!panel) return;
    const focus = document.activeElement;
    if (focus instanceof HTMLAnchorElement && this.contains(focus)) return;
    this.#activate(this.#panels.indexOf(panel));
  };

  #onFocus = (event) => {
    const panel = this.#panelFrom(event);
    if (panel && event.target.matches(':focus-visible')) this.#activate(this.#panels.indexOf(panel), true);
  };

  #onKey = (event) => {
    if (!(event.target instanceof HTMLElement) || !event.target.matches('summary')) return;
    const panel = this.#panelFrom(event);
    if (!panel) return;
    const rtl = getComputedStyle(this).direction === 'rtl';
    const i = this.#panels.indexOf(panel);
    let next;
    if (event.key === 'ArrowRight') next = (i + (rtl ? -1 : 1) + this.#panels.length) % this.#panels.length;
    else if (event.key === 'ArrowLeft') next = (i + (rtl ? 1 : -1) + this.#panels.length) % this.#panels.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = this.#panels.length - 1;
    else return;
    event.preventDefault();
    this.#activate(next, true);
    this.#panels[next].querySelector('summary').focus({ preventScroll: true });
    if (this.#mobile.matches) this.#scrollTo(next);
  };

  /** Preserve one expanded desktop panel when its native summary is clicked. */
  selectPanel(event) {
    if (this.#mobile.matches) {
      event.preventDefault();
      return;
    }
    const panel = this.#panelFrom(event);
    if (!panel) return;
    event.preventDefault();
    this.#activate(this.#panels.indexOf(panel), event.target.matches(':focus-visible'));
  }

  /** Move the mobile carousel without intercepting vertical page scrolling. */
  selectDot(event) {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('[data-series-dot]');
    if (!button || !this.contains(button)) return;
    const index = Number(button.getAttribute('data-series-dot'));
    this.#activate(index, true);
    this.#scrollTo(index);
  }

  #scrollTo(index) {
    const panel = this.#panels[index];
    if (!panel) return;
    const track = this.refs.track;
    const rtl = getComputedStyle(this).direction === 'rtl';
    const rect = track.getBoundingClientRect();
    const target = panel.getBoundingClientRect();
    const left = rtl ? target.right - rect.right : target.left - rect.left;
    track.scrollTo({ left: track.scrollLeft + left, behavior: this.#reduceMotion.matches ? 'auto' : 'smooth' });
  }

  #markDot() {
    for (const [i, button] of this.#dots.entries()) {
      if (i === this.#index) button.setAttribute('aria-current', 'true');
      else button.removeAttribute('aria-current');
    }
  }

  #onScroll = () => {
    if (!this.#mobile.matches) return;
    window.cancelAnimationFrame(this.#scrollFrame);
    this.#scrollFrame = window.requestAnimationFrame(() => {
      const rect = this.refs.track.getBoundingClientRect();
      const rtl = getComputedStyle(this).direction === 'rtl';
      let nearest = Infinity;
      for (const [i, panel] of this.#panels.entries()) {
        const box = panel.getBoundingClientRect();
        const distance = Math.abs(rtl ? box.right - rect.right : box.left - rect.left);
        if (distance < nearest) {
          nearest = distance;
          this.#index = i;
        }
      }
      this.#markDot();
    });
  };

  #onEditorSelect = (event) => {
    const panel = this.#panelFrom(event);
    if (!panel) return;
    const index = this.#panels.indexOf(panel);
    this.#activate(index, true);
    if (this.#mobile.matches) this.#scrollTo(index);
  };
}

if (!customElements.get('cgl-series-accordion')) customElements.define('cgl-series-accordion', SeriesAccordion);
